/**
 * FavoritesService — Gestion des favoris
 * 
 * - Stockage local : AsyncStorage (rapide, toujours disponible)
 * - Sync backend : POST /favorites (opt-in, pour cross-device)
 * - Sync en arrière-plan
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { AWS_CONFIG } from '../config/aws_config';
import { getAuthToken, getUserIdFromToken } from './authHelpers';


const API = AWS_CONFIG.apiUrl;
// v1.12.629 — Clé GLOBALE (legacy) → contamination cross-account sur même device.
// Maintenant scopée par userId via getStorageKey(). La clé legacy est purgée
// à la première lecture pour éviter de la migrer (incertitude sur le owner).
const LEGACY_KEY = 'outfit_favorites_v1';

// v1.12.914 — Accepter un userId fallback (au cas où le token SecureStore est
// momentanément absent / en cours de refresh). Caller peut passer user?.id.
async function getStorageKey(fallbackUid) {
  let uid = await getUserIdFromToken();
  if (!uid && fallbackUid) uid = String(fallbackUid);
  if (!uid) return null;
  return `outfit_favorites_v1_${uid}`;
}

// v1.12.629 — Purge one-shot de la clé legacy au boot (évite que les favoris
// d'un user A persistent en local et s'affichent à un user B sur même device).
let _legacyPurged = false;
async function purgeLegacyKey() {
  if (_legacyPurged) return;
  _legacyPurged = true;
  try {
    await AsyncStorage.removeItem(LEGACY_KEY);
  } catch {}
}

// ─── Lire la liste des favoris ──────────────────────────────
// v1.12.914 — accepte un fallbackUid (pour éviter [] si SecureStore vide)
export async function getFavorites(fallbackUid) {
  try {
    await purgeLegacyKey();
    const key = await getStorageKey(fallbackUid);
    if (!key) return [];
    const raw = await AsyncStorage.getItem(key);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    console.warn('[Favorites] load:', e.message);
    return [];
  }
}

// ─── Vérifier si un produit est en favori ──────────────────
export async function isFavorite(productId) {
  const favs = await getFavorites();
  return favs.some(f => f.id === productId);
}

// ─── Ajouter aux favoris ───────────────────────────────────
// v1.12.919 — Refonte priorisation backend :
//   1. Construit favItem minimal
//   2. Sync backend (DB) — source de vérité, succès = favori vraiment sauvegardé
//   3. Cache local AsyncStorage (best-effort, peut échouer sur quota web)
//   → addFavorite renvoie true dès que backend OK, même si cache local KO
//   → l'UI verra le favori au prochain GET /favorites depuis le backend
export async function addFavorite(product, fallbackUid) {
  if (!product || !product.id) {
    console.warn('[Favorites] addFavorite : product.id manquant', { hasProduct: !!product, keys: product ? Object.keys(product).slice(0,8) : null });
    return false;
  }

  // v1.13.65 — image tronquée (thumbnail, quota) MAIS PAS les URLs de redirection :
  // les liens affiliés (linksynergy/skimlinks/CJ + tracking) dépassent souvent 500
  // caractères → les tronquer cassait le "Voir sur le site partenaire" depuis les
  // favoris. On les garde ENTIÈRES (200 favoris × ~1.5KB reste bien sous le quota).
  const truncImg = (url) => {
    if (!url || typeof url !== 'string') return undefined;
    return url.length > 500 ? url.slice(0, 500) : url;
  };
  const keepUrl = (url) => (url && typeof url === 'string' ? url : undefined);
  const favItem = {
    id:          product.id,
    partner:     product.partner,
    brand:       product.brand,
    name:        product.name?.slice(0, 200),
    category:    product.category,
    price:       product.price,
    originalPrice: product.originalPrice,
    currency:    product.currency || 'EUR',
    color:       product.color,
    image:       truncImg(product.image || product.image_url || product.photoUrl),
    emoji:       product.emoji,
    url:         keepUrl(product.url),
    originalUrl: keepUrl(product.originalUrl),
    affiliateUrl: keepUrl(product.affiliateUrl || product.affiliate_url),
    addedAt:     Date.now(),
  };

  // v1.13.65 — CACHE LOCAL D'ABORD (instantané), backend en ARRIÈRE-PLAN.
  // Avant : on attendait syncFavoriteToBackend (fetch SANS timeout) AVANT le cache
  // local → si le POST /favorites pendait (réseau lent Android), tout le toggle se
  // bloquait et le bouton cœur "ne réagissait plus". Désormais : on écrit en local
  // tout de suite (le cœur répond instantanément) et on synchronise le backend
  // sans bloquer.
  let localOk = false;
  try {
    const favs = await getFavorites(fallbackUid);
    if (favs.some(f => f.id === product.id)) {
      localOk = true; // déjà présent
    } else {
      const next = [favItem, ...favs].slice(0, 200);
      const key = await getStorageKey(fallbackUid);
      if (key) {
        try {
          await AsyncStorage.setItem(key, JSON.stringify(next));
          localOk = true;
        } catch (storageErr) {
          const msg = String(storageErr?.message || storageErr || '').toLowerCase();
          console.warn('[Favorites] cache local échec:', msg);
          if (msg.includes('quota') || msg.includes('exceeded')) {
            try {
              const keys = await AsyncStorage.getAllKeys();
              const PURGEABLE_PATTERNS = [
                /^outfit_cache_/, /^outfit_today_/, /^outfit_trips_cache/,
                /^outfit_travel_/, /^outfit_ai_/, /^outfit_created_looks_etag_/,
                /^outfit_trending_/, /^outfit_wardrobe_cache/,
                /^outfit_suggestions_/, /^outfit_recent_/,
              ];
              const toRemove = keys.filter(k => PURGEABLE_PATTERNS.some(p => p.test(k)));
              if (toRemove.length > 0) {
                await AsyncStorage.multiRemove(toRemove);
                await AsyncStorage.setItem(key, JSON.stringify(next));
                localOk = true;
                console.log('[Favorites] cache local OK après purge');
              }
            } catch (e2) { console.warn('[Favorites] purge échouée'); }
          }
        }
      } else {
        console.warn('[Favorites] pas de key locale (SecureStore vide)');
      }
    }
  } catch (e) {
    console.warn('[Favorites] cache local erreur:', e?.message);
  }

  // Backend en arrière-plan (fire-and-forget) — ne bloque JAMAIS le toggle.
  syncFavoriteToBackend('add', favItem)
    .then((ok) => { if (ok) console.log('[Favorites] backend save OK:', favItem.id); })
    .catch((e) => console.warn('[Favorites] backend sync error:', e?.message));

  return localOk || true;
}

// ─── Retirer des favoris ───────────────────────────────────
// v1.13.11 — fallbackUid (token parfois absent → getStorageKey null → remove
// échouait silencieusement et l'item réapparaissait). + match tolérant
// (String + product_id/partner_product_id) pour gérer les id hétérogènes.
export async function removeFavorite(productId, fallbackUid) {
  try {
    const favs = await getFavorites(fallbackUid);
    const pid = String(productId);
    const next = favs.filter(f =>
      String(f?.id ?? '') !== pid &&
      String(f?.product_id ?? '') !== pid &&
      String(f?.partner_product_id ?? '') !== pid
    );
    const key = await getStorageKey(fallbackUid);
    if (!key) return false; // pas d'user connecté
    await AsyncStorage.setItem(key, JSON.stringify(next));

    // Sync backend
    syncFavoriteToBackend('remove', { id: productId }).catch(() => {});
    return true;
  } catch (e) {
    console.warn('[Favorites] remove:', e.message);
    return false;
  }
}

// ─── Toggle ─────────────────────────────────────────────────
export async function toggleFavorite(product) {
  const is = await isFavorite(product.id);
  if (is) {
    await removeFavorite(product.id);
    return false;
  } else {
    await addFavorite(product);
    return true;
  }
}

// ─── Sync backend (optionnel, pour cross-device) ───────────
// v1.12.919 — Retourne true/false pour permettre à addFavorite de savoir
//   si la save backend a réussi (utile si cache local échoue par quota).
async function syncFavoriteToBackend(action, item) {
  try {
    const token = await getAuthToken();
    if (!token) return false;
    const res = await fetch(`${API}/favorites`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ action, item }),
    });
    return res.ok;
  } catch (e) { return false; }
}

// ─── Récupérer les favoris depuis le backend ───────────────
// Appelé au démarrage pour restaurer entre appareils
export async function fetchBackendFavorites() {
  try {
    const token = await getAuthToken();
    if (!token) return null;
    const res = await fetch(`${API}/favorites`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!Array.isArray(data.favorites)) return null;
    return data.favorites;
  } catch (e) {
    return null;
  }
}

// ─── Merge local + backend (pour restaurer cross-device) ────
export async function restoreFavoritesFromBackend() {
  const backend = await fetchBackendFavorites();
  if (!backend) return;
  const local = await getFavorites();
  const map = new Map();
  [...local, ...backend].forEach(f => {
    if (f && f.id) map.set(f.id, f);
  });
  const merged = Array.from(map.values()).sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0));
  const key = await getStorageKey();
  if (!key) return;
  await AsyncStorage.setItem(key, JSON.stringify(merged.slice(0, 200)));
}

// ─── Compteur pour badge ────────────────────────────────────
export async function getFavoritesCount() {
  const favs = await getFavorites();
  return favs.length;
}
